import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Leaf, ArrowRight, Users, Check, Loader2, Building2, User as UserIcon } from "lucide-react";

const WORKING_GROUPS = [
  { k: "rappresentanza", l: "Rappresentanza e rapporti con gli enti" },
  { k: "rete", l: "Sviluppo rete associativa" },
  { k: "comunicazione", l: "Comunicazione" },
  { k: "progetti", l: "Progetti e fundraising" },
  { k: "ricerca", l: "Ricerca psicologica e pedagogica" },
];

const COMM_CHANNELS = [
  { k: "email", l: "Email" },
  { k: "whatsapp", l: "WhatsApp" },
  { k: "facebook", l: "Gruppo Facebook" },
];

const KITS = [
  { k: "pannello", l: "Pannello stampato 50/70 con filiera a scelta" },
  { k: "manifesto", l: "Manifesto Adesione Network in forex 50/70" },
  { k: "libro", l: "Libro \"Educatori Esperienziali in Natura\" di Christian Mancini" },
];

export default function JoinNetwork() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", gender: "", birth_date: "",
    address: "", city: "", postal_code: "", country: "Italia",
    email: "", phone: "",
    is_legal_entity: false,
    company_name: "", company_address: "", company_city: "", company_postal_code: "",
    company_country: "Italia", company_website: "", company_email: "",
    membership_type: "new",
    communication_channels: ["email"],
    working_groups: [],
    kit_choice: "libro",
  });

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, v) => setForm((f) => ({
    ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v],
  }));

  const amount = form.membership_type === "new" ? 120 : 100;

  const canNext = () => {
    if (step === 1) return form.first_name && form.last_name && form.email && form.phone && form.address && form.city && form.postal_code;
    if (step === 2) return !form.is_legal_entity || (form.company_name && form.company_address && form.company_city);
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/memberships", form);
      const origin = window.location.origin;
      const { data: checkout } = await api.post("/memberships/checkout", {
        application_id: data.id, origin_url: origin,
      });
      window.location.href = checkout.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#FBF9F6] min-h-[calc(100vh-80px)]">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1464278533981-50106e6176b1?w=2000" alt="" className="w-full h-full object-cover"/>
          <div className="absolute inset-0 bg-gradient-to-br from-[#2C312E]/80 to-[#4A6B53]/60"/>
        </div>
        <div className="relative mx-auto max-w-5xl px-6 md:px-10 py-20 md:py-28">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur border border-white/20 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-white mb-5">
            <Leaf size={14}/> Unisciti al Network
          </div>
          <h1 className="font-display font-black text-4xl md:text-6xl text-white leading-tight max-w-3xl">
            Entra nel Network Agrigiochiamo.<br/>
            <span className="text-[#E4B871]">Campagna 2026.</span>
          </h1>
          <p className="mt-5 text-white/85 max-w-2xl text-lg">
            Fattorie didattiche, cooperative sociali, educatori e realtà in natura: uniamo le forze per portare i bambini fuori.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 md:px-10 py-14">
        {/* Stepper */}
        <div className="flex items-center gap-2 mb-10">
          {[1, 2, 3].map((n) => (
            <div key={n} className="flex items-center gap-2 flex-1">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center font-display font-black text-sm ${step >= n ? "bg-[#4A6B53] text-white" : "bg-[#E5DCD0] text-[#7A7369]"}`}>{n}</div>
              <div className="text-xs font-bold uppercase tracking-wider text-[#2C312E] hidden md:block">
                {n === 1 ? "I tuoi dati" : n === 2 ? "Azienda" : "Adesione"}
              </div>
              {n < 3 && <div className={`flex-1 h-0.5 ${step > n ? "bg-[#4A6B53]" : "bg-[#E5DCD0]"}`}/>}
            </div>
          ))}
        </div>

        <div className="rounded-[2rem] bg-white border border-[#E5DCD0] p-6 md:p-10 shadow-sm">
          {step === 1 && (
            <div data-testid="step-personal">
              <h2 className="font-display font-black text-2xl md:text-3xl text-[#2C312E] mb-1">I tuoi dati</h2>
              <p className="text-[#58625B] mb-6">Inserisci i dati della persona che richiede l'iscrizione.</p>
              <div className="grid md:grid-cols-2 gap-4">
                <F l="Nome" v={form.first_name} o={(v) => upd("first_name", v)} required testid="first-name"/>
                <F l="Cognome" v={form.last_name} o={(v) => upd("last_name", v)} required testid="last-name"/>
                <SelectF l="Sesso" v={form.gender} o={(v) => upd("gender", v)} opts={[{k:"",l:"—"},{k:"F",l:"Femmina"},{k:"M",l:"Maschio"},{k:"X",l:"Altro"}]}/>
                <F l="Data di nascita" type="date" v={form.birth_date} o={(v) => upd("birth_date", v)}/>
                <F l="Indirizzo" v={form.address} o={(v) => upd("address", v)} required testid="address"/>
                <F l="Città" v={form.city} o={(v) => upd("city", v)} required testid="city"/>
                <F l="CAP" v={form.postal_code} o={(v) => upd("postal_code", v)} required testid="postal-code"/>
                <F l="Paese" v={form.country} o={(v) => upd("country", v)}/>
                <F l="Email" type="email" v={form.email} o={(v) => upd("email", v)} required testid="email"/>
                <F l="Cellulare" v={form.phone} o={(v) => upd("phone", v)} required testid="phone"/>
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-company">
              <h2 className="font-display font-black text-2xl md:text-3xl text-[#2C312E] mb-1">Chi si iscrive</h2>
              <p className="text-[#58625B] mb-6">Ti iscrivi come persona fisica o per conto di un'azienda/associazione?</p>
              <div className="grid md:grid-cols-2 gap-3 mb-6">
                {[
                  { k: false, l: "Persona fisica", icon: UserIcon, d: "Mi iscrivo come singolo" },
                  { k: true, l: "Persona giuridica", icon: Building2, d: "Azienda, associazione, cooperativa" },
                ].map((o) => {
                  const Icon = o.icon;
                  const active = form.is_legal_entity === o.k;
                  return (
                    <button key={String(o.k)} type="button" onClick={() => upd("is_legal_entity", o.k)}
                      className={`text-left rounded-2xl border-2 p-5 transition-all ${active ? "border-[#4A6B53] bg-[#4A6B53]/5" : "border-[#E5DCD0] hover:border-[#4A6B53]/40"}`}
                      data-testid={`entity-${o.k ? "legal" : "physical"}`}>
                      <Icon size={22} className={active ? "text-[#4A6B53]" : "text-[#7A7369]"}/>
                      <div className="font-display font-extrabold text-lg text-[#2C312E] mt-2">{o.l}</div>
                      <div className="text-sm text-[#58625B]">{o.d}</div>
                    </button>
                  );
                })}
              </div>
              {form.is_legal_entity && (
                <div className="grid md:grid-cols-2 gap-4">
                  <F l="Ragione sociale" v={form.company_name} o={(v) => upd("company_name", v)} required testid="company-name"/>
                  <F l="Sito web" v={form.company_website} o={(v) => upd("company_website", v)}/>
                  <F l="Indirizzo" v={form.company_address} o={(v) => upd("company_address", v)} required/>
                  <F l="Città" v={form.company_city} o={(v) => upd("company_city", v)} required/>
                  <F l="CAP" v={form.company_postal_code} o={(v) => upd("company_postal_code", v)}/>
                  <F l="Paese" v={form.company_country} o={(v) => upd("company_country", v)}/>
                  <F l="Email aziendale" type="email" v={form.company_email} o={(v) => upd("company_email", v)}/>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-membership">
              <h2 className="font-display font-black text-2xl md:text-3xl text-[#2C312E] mb-1">Adesione e kit</h2>
              <p className="text-[#58625B] mb-6">Scegli il tipo di adesione e i servizi a cui sei interessato.</p>

              <div className="grid md:grid-cols-2 gap-3 mb-8">
                {[
                  { k: "new", amt: 120, l: "Nuovo socio", d: "Mi iscrivo per la prima volta al Network" },
                  { k: "renewal", amt: 100, l: "Rinnovo", d: "Sono già stato socio negli anni precedenti" },
                ].map((o) => {
                  const active = form.membership_type === o.k;
                  return (
                    <button key={o.k} type="button" onClick={() => upd("membership_type", o.k)}
                      className={`relative text-left rounded-2xl border-2 p-6 transition-all ${active ? "border-[#C8795A] bg-[#C8795A]/5" : "border-[#E5DCD0] hover:border-[#C8795A]/40"}`}
                      data-testid={`membership-${o.k}`}>
                      {active && <div className="absolute top-4 right-4 w-7 h-7 rounded-full bg-[#C8795A] text-white flex items-center justify-center"><Check size={14}/></div>}
                      <div className="font-display font-black text-3xl text-[#2C312E]">€{o.amt}</div>
                      <div className="font-bold text-[#2C312E] mt-1">{o.l}</div>
                      <div className="text-sm text-[#58625B] mt-1">{o.d}</div>
                    </button>
                  );
                })}
              </div>

              <Group label="Canali di comunicazione preferiti">
                {COMM_CHANNELS.map((c) => (
                  <Chip key={c.k} label={c.l} active={form.communication_channels.includes(c.k)}
                    onClick={() => toggle("communication_channels", c.k)} testid={`comm-${c.k}`}/>
                ))}
              </Group>
              <Group label="Gruppi di lavoro a cui vorrei partecipare">
                {WORKING_GROUPS.map((g) => (
                  <Chip key={g.k} label={g.l} active={form.working_groups.includes(g.k)}
                    onClick={() => toggle("working_groups", g.k)} testid={`group-${g.k}`}/>
                ))}
              </Group>
              <Group label="Kit associativo preferito">
                <div className="grid md:grid-cols-3 gap-3 w-full">
                  {KITS.map((k) => {
                    const active = form.kit_choice === k.k;
                    return (
                      <button key={k.k} type="button" onClick={() => upd("kit_choice", k.k)}
                        className={`text-left rounded-2xl border-2 p-4 text-sm ${active ? "border-[#4A6B53] bg-[#4A6B53]/5 font-bold text-[#2C312E]" : "border-[#E5DCD0] text-[#58625B]"}`}
                        data-testid={`kit-${k.k}`}>
                        {k.l}
                      </button>
                    );
                  })}
                </div>
              </Group>

              <div className="mt-8 rounded-2xl bg-[#4A6B53]/5 border border-[#4A6B53]/20 p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#4A6B53] text-white flex items-center justify-center shrink-0 mt-0.5">
                    <Leaf size={16}/>
                  </div>
                  <div>
                    <div className="font-display font-extrabold text-[#2C312E]">Aderisci al Manifesto del Network</div>
                    <p className="text-sm text-[#58625B] mt-1 leading-relaxed">
                      Iscrivendoti al Network sottoscrivi i <Link to="/chi-siamo#manifesto" target="_blank" className="underline font-bold text-[#4A6B53] hover:text-[#3D5844]">sette principi del nostro Manifesto</Link>: Outdoor Education,
                      Comunicazione Educativa, Ricerca Pedagogica, Visione Olistica della Natura,
                      Cultura Rurale, Valore delle Relazioni e Metodologia Ludica.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 rounded-2xl bg-[#F2EBE1] p-5 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#7A7369]">Totale quota associativa</div>
                  <div className="font-display font-black text-3xl text-[#2C312E]">€{amount},00</div>
                </div>
                <div className="text-xs text-[#58625B] max-w-xs">Il pagamento è sicuro e viene gestito da <span className="font-bold">Stripe</span>. Non memorizziamo i dati della tua carta.</div>
              </div>
            </div>
          )}

          {/* Nav */}
          <div className="mt-10 flex justify-between">
            <button type="button" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1}
              className="rounded-full border-2 border-[#E5DCD0] px-6 py-3 text-sm font-bold disabled:opacity-40 hover:border-[#4A6B53]" data-testid="step-back">
              Indietro
            </button>
            {step < 3 ? (
              <button type="button" onClick={() => canNext() && setStep((s) => s + 1)} disabled={!canNext()}
                className="inline-flex items-center gap-2 rounded-full bg-[#4A6B53] text-white px-6 py-3 text-sm font-bold hover:bg-[#3D5844] disabled:opacity-50" data-testid="step-next">
                Continua <ArrowRight size={16}/>
              </button>
            ) : (
              <button type="button" onClick={submit} disabled={submitting}
                className="inline-flex items-center gap-2 rounded-full bg-[#C8795A] text-white px-6 py-3 text-sm font-bold hover:bg-[#B3684B] disabled:opacity-60" data-testid="submit-membership">
                {submitting ? <Loader2 size={16} className="animate-spin"/> : <Users size={16}/>}
                Paga €{amount} e iscriviti
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function F({ l, v, o, type = "text", required, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-[#7A7369]">{l}{required && " *"}</span>
      <input type={type} value={v ?? ""} onChange={(e) => o(e.target.value)} required={required}
        className="mt-2 w-full h-12 rounded-xl border-2 border-[#E5DCD0] bg-white px-4 focus:outline-none focus:ring-2 focus:ring-[#4A6B53] focus:border-transparent"
        data-testid={testid}/>
    </label>
  );
}
function SelectF({ l, v, o, opts }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-[#7A7369]">{l}</span>
      <select value={v} onChange={(e) => o(e.target.value)} className="mt-2 w-full h-12 rounded-xl border-2 border-[#E5DCD0] bg-white px-4 focus:outline-none focus:ring-2 focus:ring-[#4A6B53]">
        {opts.map((x) => <option key={x.k} value={x.k}>{x.l}</option>)}
      </select>
    </label>
  );
}
function Group({ label, children }) {
  return (
    <div className="mt-6">
      <div className="text-xs font-bold uppercase tracking-wider text-[#7A7369] mb-3">{label}</div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}
function Chip({ label, active, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all ${active ? "bg-[#4A6B53] text-white border-[#4A6B53]" : "bg-white text-[#58625B] border-[#E5DCD0] hover:bg-[#F2EBE1]"}`}>
      {label}
    </button>
  );
}
